import { useEffect, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { Modal } from "@components/input";
import type { SavedQueryCreate, SavedQuerySpec, SavedQueryVisibility } from "@api/types";

export interface SaveViewDialogProps {
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onSave: (payload: SavedQueryCreate) => void | Promise<void>;
  /**
   * The spec being captured. In the real flow a source screen ("Save this
   * view") passes its current filter/pivot spec; opened standalone from the
   * library there is none, and the dialog shows a hint that a view is captured
   * from a screen. Stored verbatim as the query's `spec`.
   */
  spec?: SavedQuerySpec | null;
  /** Prefill the name (e.g. a suggested name from the source screen). */
  defaultName?: string;
}

/**
 * Save-a-view dialog (§4). Names + describes + sets visibility on a captured
 * spec, then POSTs a new saved query. Built on the shared portaled `Modal`. The
 * spec itself is not editable here — it is captured from the originating screen;
 * the dialog previews it read-only so the user knows what they're saving.
 */
export function SaveViewDialog({
  open,
  saving,
  onClose,
  onSave,
  spec = null,
  defaultName = "",
}: SaveViewDialogProps) {
  const { t } = useLocale();
  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<SavedQueryVisibility>("private");

  // Reset fields whenever the dialog (re)opens so a prior draft never leaks in.
  useEffect(() => {
    if (open) {
      setName(defaultName);
      setDescription("");
      setVisibility("private");
    }
  }, [open, defaultName]);

  const canSave = name.trim().length > 0 && !saving;

  const submit = () => {
    if (!canSave) return;
    void onSave({
      name: name.trim(),
      description: description.trim() || null,
      spec: (spec ?? {}) as unknown as Record<string, unknown>,
      visibility,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("saved.dialog.title")}
      width={560}
      footerNote={t("saved.dialog.specNote")}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {t("saved.dialog.cancel")}
          </button>
          <button type="button" className="btn primary" disabled={!canSave} onClick={submit}>
            {saving ? t("saved.dialog.saving") : t("saved.dialog.save")}
          </button>
        </>
      }
    >
      <div className="field save-field">
        <label htmlFor="sv-name">{t("saved.dialog.name")}</label>
        <input
          id="sv-name"
          type="text"
          value={name}
          placeholder={t("saved.dialog.namePlaceholder")}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="field save-field">
        <label htmlFor="sv-desc">{t("saved.dialog.description")}</label>
        <textarea
          id="sv-desc"
          rows={2}
          className="text-area"
          value={description}
          placeholder={t("saved.dialog.descriptionPlaceholder")}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="field save-field">
        <label htmlFor="sv-vis">{t("saved.dialog.visibility")}</label>
        <select
          id="sv-vis"
          value={visibility}
          onChange={(e) => setVisibility(e.target.value as SavedQueryVisibility)}
        >
          <option value="private">{t("saved.dialog.visibility.private")}</option>
          <option value="public">{t("saved.dialog.visibility.public")}</option>
        </select>
      </div>

      <div className="field save-field">
        <label>{t("saved.dialog.opensIn")}</label>
        {spec ? (
          <pre className="save-spec-preview">{JSON.stringify(spec, null, 2)}</pre>
        ) : (
          <p className="hint save-nospec">{t("saved.dialog.noSpecHint")}</p>
        )}
      </div>
    </Modal>
  );
}
